// @flow
import React from "react";
import { withGlobalContext } from "../../../common/GlobalContext";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";

import type { $Agent, $Site, $SiteOffice, $Team } from "flow/types";

type $Props = {
  agent: $Agent,
  classes: Object,
  site: $Site,
  team: $Team,
  office: $SiteOffice,
};

const ContactInfo = ({ agent, classes, site, team, office }: $Props) => {
  if (!site.officeId && site.userId && agent) {
    return (
      <div className={classes.root}>
        {agent.fullName ? (
          <Typography
            className={classes.subheading}
            type="heading6"
            gutterBottom
          >
            {agent.fullName}
          </Typography>
        ) : null}
        {agent.title ? (
          <Typography type="body" className={classes.title}>
            {agent.title}
          </Typography>
        ) : null}
        {agent.cellPhone ? (
          <Typography type="body" className={classes.text}>
            <a
              href={`tel:${agent.cellPhone}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Cell: {agent.cellPhoneFormatted}
            </a>
          </Typography>
        ) : null}
        {agent.directPhone ? (
          <Typography type="body" className={classes.text}>
            <a
              href={`tel:${agent.directPhone}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Direct: {agent.directPhoneFormatted}
            </a>
          </Typography>
        ) : null}
        {agent.fax ? (
          <Typography type="body" className={classes.text}>
            <a
              href={`tel:${agent.fax}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Fax: {agent.faxFormatted}
            </a>
          </Typography>
        ) : null}
        {office && office.officePhone ? (
          <Typography type="body" className={classes.text}>
            <a
              href={`tel:${office.officePhone}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Office: {office.officePhoneFormatted}
            </a>
          </Typography>
        ) : null}
        {agent.email ? (
          <Typography type="body" className={classes.text}>
            <a href="/contact">{agent.email}</a>
          </Typography>
        ) : office && office.email ? (
          <Typography type="body" className={classes.text}>
            <a href="/contact">{office.email}</a>
          </Typography>
        ) : null}
      </div>
    );
  } else if (site.teamId && team) {
    const urlFriendlyAddress: string = encodeURIComponent(
      `${team.fullStreetAddress || ""} ${team.cityStateZip}`
    );
    return (
      <div className={classes.root}>
        {team.logoPath ? (
          <div className={classes.officeLogo}>
            <img
              src={team.logoPath}
              alt={team.name || ""}
              title={team.name || ""}
            />
          </div>
        ) : null}
        {team.name ? (
          <Typography className={classes.subheading} type="heading6">
            {team.name}
          </Typography>
        ) : null}
        {team.cellPhone ? (
          <Typography type="body" className={classes.text}>
            <a
              href={`tel:${team.cellPhone}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Cell: {team.cellPhoneFormatted}
            </a>
          </Typography>
        ) : null}
        {team.directPhone ? (
          <Typography type="body" className={classes.text}>
            <a
              href={`tel:${team.directPhone}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Direct: {team.directPhoneFormatted}
            </a>
          </Typography>
        ) : null}
        {team.email ? (
          <Typography type="body" className={classes.text}>
            <a href="/contact">{team.email}</a>
          </Typography>
        ) : null}
        {team.streetAddress1 ? (
          <Typography type="body" className={classes.text}>
            <a
              href={`https://maps.google.com/?q=${urlFriendlyAddress}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {team.streetAddress1}
            </a>
          </Typography>
        ) : null}
        {team.streetAddress2 ? (
          <Typography type="body" className={classes.text}>
            <a
              href={`https://maps.google.com/?q=${urlFriendlyAddress}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {team.streetAddress2}
            </a>
          </Typography>
        ) : null}
        {team.cityStateZip ? (
          <Typography type="body2" className={classes.text}>
            <a
              href={`https://maps.google.com/?q=${urlFriendlyAddress}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {team.cityStateZip}
            </a>
          </Typography>
        ) : null}
      </div>
    );
  } else {
    return null;
  }
};

const styles = (theme) => {
  return {
    root: {
      "& p": {
        margin: "0 auto",
        color: theme.colors.primaryFontColor,
      },
      "& a": {
        color: theme.colors.primaryFontColor,
      },
    },
    subheading: {
      marginTop: "0",
      color: theme.colors.primaryFontColor,
    },
    title: {
      fontSize: "16px",
      fontWeight: "bold",
      color: theme.colors.primaryFontColor,
    },
    officeLogo: {
      position: "relative",
      maxWidth: "130px",
      margin: "0 auto",
      "& img": {
        display: "block",
        width: "100%",
        height: "auto",
      },
    },
    text: {
      color: theme.colors.primaryFontColor,
      fontSize: "14px",
      lineHeight: "18px",
    },
  };
};

export default withGlobalContext(withStyles(styles)(ContactInfo));
